import { ComponentProps, FC, PropsWithChildren, ReactElement, cloneElement } from 'react';
import { Box, ButtonV2, FelaCSS, TColor } from '@bridebook/ui';
import { ms } from '@bridebook/ui/src/fela-utils/ms';
import { useViewModeTheme } from '@bridebook/web/components/wedding-website/view-mode/view-mode.context';
import { PALETTE_BACKGROUNDS } from '@bridebook/web/lib/wedding-website/constants/themes';
import { Palettes } from '@bridebook/web/lib/wedding-website/types';
import styles from './tile-component.style';

interface ISubcomponents {
  Container: typeof Container;
  ItemGroup: typeof ItemGroup;
  Wrapper: typeof Wrapper;
  MainTitle: typeof MainTitle;
  Title: typeof Title;
  TextHeading: typeof TextHeading;
  Text: typeof Text;
  Button: typeof Button;
  TextPlaceholder: typeof TextPlaceholder;
}

export const enum WebsiteTileVariant {
  Primary = 'primary',
  Secondary = 'secondary',
  Tertiary = 'tertiary',
}

interface IWebsiteTileVariantProps extends PropsWithChildren {
  title?: string | React.ReactNode;
  variant: WebsiteTileVariant;
  style?: FelaCSS;
}

interface IProps extends IWebsiteTileVariantProps {
  palette: Palettes;
}

const Wrapper: FC<ComponentProps<typeof Box>> = (props) => (
  <Box {...props} style={ms(styles.wrapper, props.style)} />
);
const Container: FC<ComponentProps<typeof Box>> = (props) => <Box {...props} gap={10} />;
const ItemGroup: FC<ComponentProps<typeof Box>> = (props) => <Box {...props} gap={4} />;
const MainTitle: FC<ComponentProps<typeof Box>> = ({ style, ...props }) => {
  const themeStyles = useViewModeTheme();
  return <Box {...props} style={ms(themeStyles.h1, styles.textWrapper, style)} />;
};
const Title: FC<ComponentProps<typeof Box>> = ({ style, ...props }) => {
  const themeStyles = useViewModeTheme();
  return <Box {...props} style={ms(themeStyles.h2, styles.textWrapper, style)} />;
};
const TextHeading: FC<ComponentProps<typeof Box>> = ({ style, ...props }) => {
  const themeStyles = useViewModeTheme();
  return <Box {...props} style={ms(themeStyles.h3, styles.textWrapper, style)} />;
};
const Text: FC<ComponentProps<typeof Box>> = ({ style, ...props }) => {
  const themeStyles = useViewModeTheme();
  return <Box {...props} style={ms(themeStyles.p, styles.textWrapper, style)} />;
};
const TextPlaceholder: FC<ComponentProps<typeof Box>> = ({ style, ...props }) => (
  <Box {...props} style={ms(styles.textPlaceholder, style)} />
);
const Button: FC<ComponentProps<typeof ButtonV2>> = ({
  children,
  styleWrapper,
  startIcon,
  endIcon,
  ...props
}) => {
  const themeStyles = useViewModeTheme();
  const wrappedStartIcon = startIcon && (
    <Box>
      {cloneElement(startIcon as ReactElement, {
        color: themeStyles.buttonText.color as unknown as TColor,
      })}
    </Box>
  );
  const wrappedEndIcon = endIcon && (
    <Box>
      {cloneElement(endIcon as ReactElement, {
        color: themeStyles.buttonText.color as unknown as TColor,
      })}
    </Box>
  );

  return (
    <ButtonV2
      {...props}
      startIcon={wrappedStartIcon}
      endIcon={wrappedEndIcon}
      styleWrapper={ms(themeStyles.button, styles.button, styleWrapper)}>
      <Box style={themeStyles.buttonText}>{children}</Box>
    </ButtonV2>
  );
};

const WeddingWebsiteTileBridebook: FC<IWebsiteTileVariantProps> = ({
  title,
  variant,
  children,
  style,
}) => {
  const backgroundUrl =
    variant === WebsiteTileVariant.Primary
      ? PALETTE_BACKGROUNDS[Palettes.Bridebook].tileBackground
      : PALETTE_BACKGROUNDS[Palettes.Bridebook].tileBackgroundAlt;
  return (
    <Box style={ms(styles.containerWithImage(backgroundUrl), style)}>
      {title && <Title style={styles.title}>{title}</Title>}
      {children}
    </Box>
  );
};

const WeddingWebsiteTileRoses: FC<IWebsiteTileVariantProps> = ({
  title,
  variant,
  children,
  style,
}) => {
  const isPrimary = variant === WebsiteTileVariant.Primary;
  return (
    <Box style={ms(isPrimary ? styles.container : styles.containerRoses, style)}>
      {!isPrimary && <Box style={styles.paletteRosesAsset('top-left')} />}
      {!isPrimary && <Box style={styles.paletteRosesAsset('bottom-right')} />}
      {title && <Title style={styles.title}>{title}</Title>}
      {children}
    </Box>
  );
};

const WeddingWebsiteTileFoliage: FC<IWebsiteTileVariantProps> = ({
  title,
  variant,
  children,
  style,
}) => {
  const isPrimary = variant === WebsiteTileVariant.Primary;
  return (
    <Box style={ms(styles.containerFoliage(variant), style)}>
      {title && (
        <Title style={styles.title}>
          <Box style={styles.titleTextWrapper}>
            {!isPrimary && <Box style={styles.paletteFoliageAsset('left')} />}
            {title}
            {!isPrimary && <Box style={styles.paletteFoliageAsset('right')} />}
          </Box>
        </Title>
      )}
      {children}
    </Box>
  );
};

const WeddingWebsiteTileWhimsical: FC<IWebsiteTileVariantProps> = ({
  title,
  variant,
  children,
  style,
}) => {
  const isPrimary = variant === WebsiteTileVariant.Primary;
  return (
    <Box style={ms(styles.containerWhimsical(variant), style)}>
      {title && (
        <Title style={styles.title}>
          <Box style={styles.titleTextWrapper}>
            {!isPrimary && <Box style={styles.paletteWhimsicalAsset('left')} />}
            {title}
            {!isPrimary && <Box style={styles.paletteWhimsicalAsset('right')} />}
          </Box>
        </Title>
      )}
      {children}
    </Box>
  );
};

export const WeddingWebsiteTileComponent: FC<IProps> & ISubcomponents = ({
  children,
  title,
  variant,
  palette,
  style,
}) => {
  switch (palette) {
    case Palettes.Bridebook: {
      return (
        <WeddingWebsiteTileBridebook title={title} variant={variant} style={style}>
          {children}
        </WeddingWebsiteTileBridebook>
      );
    }
    case Palettes.Roses: {
      return (
        <WeddingWebsiteTileRoses title={title} variant={variant} style={style}>
          {children}
        </WeddingWebsiteTileRoses>
      );
    }
    case Palettes.Foliage: {
      return (
        <WeddingWebsiteTileFoliage title={title} variant={variant} style={style}>
          {children}
        </WeddingWebsiteTileFoliage>
      );
    }
    case Palettes.Whimsical: {
      return (
        <WeddingWebsiteTileWhimsical title={title} variant={variant} style={style}>
          {children}
        </WeddingWebsiteTileWhimsical>
      );
    }
    default:
      return (
        <WeddingWebsiteTileBridebook
          title={title}
          variant={WebsiteTileVariant.Primary}
          style={style}>
          {children}
        </WeddingWebsiteTileBridebook>
      );
  }
};

WeddingWebsiteTileComponent.Container = Container;
WeddingWebsiteTileComponent.ItemGroup = ItemGroup;
WeddingWebsiteTileComponent.Wrapper = Wrapper;
WeddingWebsiteTileComponent.MainTitle = MainTitle;
WeddingWebsiteTileComponent.Title = Title;
WeddingWebsiteTileComponent.TextHeading = TextHeading;
WeddingWebsiteTileComponent.Text = Text;
WeddingWebsiteTileComponent.Button = Button;
WeddingWebsiteTileComponent.TextPlaceholder = TextPlaceholder;
